/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  CommonActions,
  CostCenterFinderDialogConfig,
  costCenterFinderDialogRoute,
  CostCenterFinderListItem,
  EDocDetailsDialogConfig,
  edocDetailsDialogRoute,
  FormActionPipelines,
  FormFlowState,
  jobCsCreateDialogRoute,
  REPORT_JOB_COLOR_SELECTION,
} from '@gh/builder-shared';
import { createLinkFromAttachment } from '@gh/builder-shared-data';
import { textMessageDialogRoute } from '@gh/common-internal-shared';
import {
  ActionFlow,
  andActions,
  AppRouter,
  booleanStreamAction,
  compoundLogicalAction,
  ContainerState,
  eitherAction,
  exitAction,
  FormStateSnapshot,
  getAllNewSelectedEntities,
  getRouteInitData,
  getRouteParam,
  getRouteQueryParam,
  goToStep,
  isDirty,
  LogicalAction,
  mapAction,
  mapFlowStateAction,
  PAGE_STATE,
  queryDirty,
  queryFormMode,
  setEntityField,
  sideEffectAction,
  State,
  UserContext,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import {
  DialogService,
  FormMode,
  isCreateFormMode,
  NotificationService,
  TextMessageDialogConfig,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { openInNewWindow } from '@gh/core-util';
import { EMPTY$, of$ } from '@gh/rx';
import { filter$, map$, switchMap$ } from '@gh/rx/operators';
import { first, head, identity, isEmpty, tail } from 'lodash';
import { Observable } from 'rxjs';
import { JobCsDetailsBusinessService } from '../../resources/job-cs-details-business.service';
import {
  getJobCsDetailsCcId,
  getJobCsDetailsHeadingId,
  getJobCsDetailsItemId,
  getJobCsDetailsSubheadingId,
  JobCsDetails,
  JobCsDetailsCc,
  jobCsDetailsCcClone,
  jobCsDetailsCcFromFinder,
  JobCsDetailsHeading,
  JobCsDetailsItem,
  JobCsDetailsMasterInfo,
  JobCsDetailsSubheading,
} from '../../resources/job-cs-details.model';
import {
  buildItemPathName,
  JobCsDetailsSettings,
  JobCsDetailsState,
  JobCsDetailsStateEventType,
  JobCsItemPath,
  queryJobCsCurrentCostCenters,
  queryJobCsCurrentSubheadingCostCenters,
  queryJobCsDocSigningId,
  queryJobCsEmptyItems,
  queryJobCsHadEmptyItems,
  queryJobCsHasErrors,
  queryJobCsHeadings,
  queryJobCsId,
  queryJobCsMasterInfoHasErrors,
  queryJobCsQuoteChanges,
  queryJobCsSaveModel,
  queryJobCsSelectedHeadingId,
  queryJobCsSelectedItemId,
  queryJobCsSelectedSubheadingId,
  queryJobCsSettings,
} from './job-cs-details.state';

export type JobCsDetailsSaveFlowState = FormFlowState<JobCsDetailsState>;

type JobCsDetailsValidateCcFlowState = { emptyItems: JobCsItemPath[]; currentItem?: JobCsItemPath };

@Injectable()
export class JobCsDetailsCommandService {
  private reportUrl: string;

  constructor(private route: ActivatedRoute,
              private userContext: UserContext,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private waitings: WaitingService,
              private dialogs: DialogService,
              private notifications: NotificationService,
              private router: AppRouter,
              private translations: TranslationService,
              @Inject(PAGE_STATE) private form: ContainerState<JobCsDetailsState>,
              private state: State<JobCsDetailsState>,
              private business: JobCsDetailsBusinessService) {
    const { reportUrl } = getRouteInitData(this.route);
    this.reportUrl = reportUrl;
  }

  completeFlow(): ActionFlow<JobCsDetailsSaveFlowState> {
    return this.pipelines.complete<any>({
      successMessage: 'msg_job_cs_successfully',
      validate: andActions(
        // validate if we have invalid data on the page
        this.commonActions.validateWithEffect(
          () => this.state.project(queryJobCsHasErrors),
          () => this.handleErrors()),
        // validate if we have empty items
        mapFlowStateAction(
          () => ({ emptyItems: this.state.project(queryJobCsEmptyItems) }),
          identity,
          this.validateCcAction()),
        // validate if we have unprocessed quote changes
        this.validateQuoteChangesAction()),
      execute: this.commonActions.execute(() => this.business.updateColorSelection(
        this.state.project(queryJobCsId),
        this.state.project(queryJobCsSaveModel))),
      finish: (state: JobCsDetailsSaveFlowState) => {
        const { value } = state;
        this.state.mutate({ type: JobCsDetailsStateEventType.Merge, details: value });

        if (isCreateFormMode(this.state.project(queryFormMode))) {
          this.router.switchTo(
            ['/job-admin/jobs', this.state.project(queryJobCsId), 'color-selection'],
            { omitQueryParams: ['csMasterId'] });
        }
      },
    });
  }

  validateCcAction(): LogicalAction<JobCsDetailsValidateCcFlowState> {
    return compoundLogicalAction('success', 'fail', {
      // continue only if #CC_REQUIRED was checked and we have empty items
      'start': eitherAction(({ emptyItems }) =>
        this.state.project(queryJobCsSettings).ccRequired && emptyItems.length > 0)
        .bindAs('ifWasSavedWithEmptyItems', 'complete.success'),
      // check if color selection was saved before with empty items
      'ifWasSavedWithEmptyItems': eitherAction(() => this.state.project(queryJobCsHadEmptyItems))
        .bindAs('ifWasSavedWithEmptyItems.yes', 'ifWasSavedWithEmptyItems.no'),
      // if color selection was saved before with empty items =>
      // continue processing and show warnings for ALL empty items
      'ifWasSavedWithEmptyItems.yes': sideEffectAction(({ emptyItems }: JobCsDetailsValidateCcFlowState) =>
        emptyItems.forEach((item) => this.notifications.warning('msg_cs_no_cc', { name: buildItemPathName(item) })))
        .bindAs('complete.success'),
      // if color selection was NOT saved before with empty items => ask for each empty item if it should stay empty
      'ifWasSavedWithEmptyItems.no': goToStep('hasNext'),
      // check whether we have one more empty item or do not
      'hasNext': eitherAction((state: JobCsDetailsValidateCcFlowState) =>
        isEmpty(state.emptyItems)).bindAs('complete.success', 'next'),
      // take next empty item
      'next': mapAction(({ emptyItems }: JobCsDetailsValidateCcFlowState) => ({
        currentItem: head(emptyItems),
        emptyItems: tail(emptyItems),
      })).bindAs('confirm'),
      // show confirmation for user:
      // he/she can continue validation process (YES) or navigate to the last empty item (NO)
      'confirm': booleanStreamAction(({ currentItem }) =>
        this.dialogs.confirmation(yesNoConfirmation('msg_cs_cc_required', {
          // tslint:disable-next-line:no-non-null-assertion
          name: buildItemPathName(currentItem!),
        })).pipe(map$(yesAnswer))).bindAs('hasNext', 'navigate'),
      // navigate to the last empty item
      // tslint:disable-next-line:no-non-null-assertion no-magic-numbers
      'navigate': sideEffectAction(({ currentItem }) => this.selectItemByPath(currentItem!)).bindAs('complete.fail'),
      'complete.success': exitAction('success'),
      '*': exitAction('fail'),
    });
  }

  load(details: JobCsDetails, mode?: FormMode, markAsDirty?: boolean): void {
    this.state.mutate({ type: JobCsDetailsStateEventType.Load, details, mode, markAsDirty });

    const heading = first(this.state.project(queryJobCsHeadings));
    if (heading) {
      this.headingSelect(getJobCsDetailsHeadingId(heading));
    }
  }

  setMasterInfo(masterInfo: FormStateSnapshot<JobCsDetailsMasterInfo>): void {
    this.state.mutate({ type: JobCsDetailsStateEventType.SetMasterInfo, masterInfo });
  }

  reset(): void {
    const { route } = this;
    const jobId = getRouteParam(route, 'jobId');
    const csMasterId = getRouteQueryParam(route, 'csMasterId');

    this.dialogs.confirmation(yesNoConfirmation('msg_reset')).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.business.getColorSelection(jobId, csMasterId).pipe(this.waitings.operator())))
      .subscribe((details) => this.load(details));
  }

  replace(): void {
    const { route } = this;
    const jobId = getRouteParam(route, 'jobId');

    this.dialogs.confirmation(yesNoConfirmation('msg_cs_replace')).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.dialogs.openWithInput(jobCsCreateDialogRoute, {
        isFromCreateBtn: false,
      }).afterClosed()),
      filter$(Boolean),
      switchMap$((csm: any) => this.business.getColorSelection(jobId, csm.master.id)))
      .subscribe((details) => this.load(details, void 0, true));
  }

  openDocument(): void {
    const documentId = this.state.project(queryJobCsDocSigningId);

    if (documentId) {
      this.dialogs.openWithInput(edocDetailsDialogRoute, <EDocDetailsDialogConfig>{ documentId });
    }
  }

  preview(): void {
    if (isCreateFormMode(this.state.project(queryFormMode)) || this.state.project(isDirty)) {
      this.notifications.error('msg_save_first');
    } else if (!this.userContext.hasPermission(REPORT_JOB_COLOR_SELECTION.permission)) {
      this.notifications.error('msg_denied_no_permissions');
    } else {
      openInNewWindow(this.reportUrl);
    }
  }

  produce({jobId, csMasterId, mode}: any): void {
    if (isCreateFormMode(this.state.project(queryFormMode)) || this.state.project(isDirty)) {
      this.notifications.error('msg_save_first');
    } else {
      this.business.produce(this.state.project(queryJobCsId)).pipe(
        this.waitings.operator())
        .subscribe(
          (attachment) => {
            this.business.getColorSelection(jobId, csMasterId).subscribe((res) => {
              this.load(res, mode);
            });
            this.dialogs.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
              title: this.translations.get('lbl_link_job_color_selection'),
              // tslint:disable-next-line:max-line-length
              html: `${this.translations.get('lbl_view_the_document_by_the_link')}: ${createLinkFromAttachment(
                attachment)}`,
              buttons: [{ title: this.translations.get('lbl_ok'), value: 'ok', type: 'primary' }],
            });
          },
          this.notifications.processAll);
    }
  }

  autoSave(): Observable<any> {
    if (!this.state.project(queryDirty)) {
      return EMPTY$;
    }

    return this.dialogs.confirmation(yesNoConfirmation('msg_unsaved_info')).pipe(
      filter$(yesAnswer),
      switchMap$(() => {
        const flow = this.completeFlow();

        return flow.run({ trigger: 'process', value: this.state.value, form: this.form }).toFinalState();
      }));
  }

  resetFocusedCell(): void {
    this.state.mutate({ type: JobCsDetailsStateEventType.ResetFocusedCell });
  }

  setHeadingField(id: ID, name: string, value: any): void {
    this.state.mutate({ type: JobCsDetailsStateEventType.HeadingSetField, id, name, value });
  }

  headingAdd(): void {
    const heading = this.business.createNewHeading();
    this.state.mutate({ type: JobCsDetailsStateEventType.HeadingAdd, entity: heading });
  }

  headingClone(heading: JobCsDetailsHeading): void {
    const cloned = this.business.cloneHeading(heading);
    this.state.mutate({
      type: JobCsDetailsStateEventType.HeadingAdd,
      entity: cloned,
      afterId: getJobCsDetailsHeadingId(heading),
    });
  }

  headingRemove(heading: JobCsDetailsHeading): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_cs_heading_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.state.mutate({
        type: JobCsDetailsStateEventType.HeadingRemove,
        id: getJobCsDetailsHeadingId(heading),
      }));
  }

  headingSelect(headingId: ID): void {
    this.state.mutate({ type: JobCsDetailsStateEventType.SelectHeading, headingId });
  }

  headingMove(from: JobCsDetailsHeading, to: JobCsDetailsHeading): void {
    this.state.mutate({
      type: JobCsDetailsStateEventType.HeadingMove,
      fromId: getJobCsDetailsHeadingId(from),
      toId: getJobCsDetailsHeadingId(to),
    });
  }

  setSubheadingField(id: ID, name: string, value: any): void {
    this.state.mutate({
      type: JobCsDetailsStateEventType.SubheadingSetField,
      headingId: this.state.project(queryJobCsSelectedHeadingId),
      id,
      name,
      value,
    });
  }

  subheadingAdd(): void {
    const subheading = this.business.createNewSubheading();
    this.state.mutate({
      type: JobCsDetailsStateEventType.SubheadingAdd,
      headingId: this.state.project(queryJobCsSelectedHeadingId),
      entity: subheading,
    });
  }

  subheadingClone(subheading: JobCsDetailsSubheading): void {
    const cloned = this.business.cloneSubheading(subheading);
    this.state.mutate({
      type: JobCsDetailsStateEventType.SubheadingAdd,
      headingId: this.state.project(queryJobCsSelectedHeadingId),
      entity: cloned,
      afterId: getJobCsDetailsSubheadingId(subheading),
    });
  }

  subheadingRemove(subheading: JobCsDetailsSubheading): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_cs_sub_heading_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.state.mutate({
        type: JobCsDetailsStateEventType.SubheadingRemove,
        headingId: this.state.project(queryJobCsSelectedHeadingId),
        id: getJobCsDetailsSubheadingId(subheading),
      }));
  }

  subheadingSelect(subheadingId: ID): void {
    this.state.mutate({
      type: JobCsDetailsStateEventType.SelectSubheading,
      headingId: this.state.project(queryJobCsSelectedHeadingId),
      subheadingId,
    });
  }

  setSubheadingMemo(subheadingId: ID, memo: string): void {
    this.setSubheadingField(subheadingId, 'memo', memo);
  }

  subheadingMove(from: JobCsDetailsSubheading, to: JobCsDetailsSubheading): void {
    this.state.mutate({
      type: JobCsDetailsStateEventType.SubheadingMove,
      headingId: this.state.project(queryJobCsSelectedHeadingId),
      fromId: getJobCsDetailsSubheadingId(from),
      toId: getJobCsDetailsSubheadingId(to),
    });
  }

  setItemField(id: ID, name: string, value: any): void {
    this.state.mutate({
      type: JobCsDetailsStateEventType.ItemSetField,
      headingId: this.state.project(queryJobCsSelectedHeadingId),
      subheadingId: this.state.project(queryJobCsSelectedSubheadingId),
      id,
      name,
      value,
    });
  }

  itemAdd(): void {
    const costCenters = this.state.project(queryJobCsSettings).ccClone
      ? this.state.project(queryJobCsCurrentSubheadingCostCenters).map(jobCsDetailsCcClone) : [];
    const item = this.business.createNewItem(costCenters);
    this.state.mutate({
      type: JobCsDetailsStateEventType.ItemAdd,
      headingId: this.state.project(queryJobCsSelectedHeadingId),
      subheadingId: this.state.project(queryJobCsSelectedSubheadingId),
      entity: item,
    });
  }

  itemRemove(item: JobCsDetailsItem): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_cs_item_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.state.mutate({
        type: JobCsDetailsStateEventType.ItemRemove,
        headingId: this.state.project(queryJobCsSelectedHeadingId),
        subheadingId: this.state.project(queryJobCsSelectedSubheadingId),
        id: getJobCsDetailsItemId(item),
      }));
  }

  itemSelect(itemId: ID): void {
    this.state.mutate({
      type: JobCsDetailsStateEventType.SelectItem,
      headingId: this.state.project(queryJobCsSelectedHeadingId),
      subheadingId: this.state.project(queryJobCsSelectedSubheadingId),
      itemId,
    });
  }

  selectItemByPath(path: JobCsItemPath): void {
    this.state.mutate({
      type: JobCsDetailsStateEventType.SelectItem,
      headingId: getJobCsDetailsHeadingId(path[0]),
      subheadingId: getJobCsDetailsSubheadingId(path[1]),
      itemId: getJobCsDetailsItemId(path[2]), // tslint:disable-line:no-magic-numbers
    });
  }

  itemMove(from: JobCsDetailsItem, to: JobCsDetailsItem): void {
    this.state.mutate({
      type: JobCsDetailsStateEventType.ItemMove,
      headingId: this.state.project(queryJobCsSelectedHeadingId),
      subheadingId: this.state.project(queryJobCsSelectedSubheadingId),
      fromId: getJobCsDetailsItemId(from),
      toId: getJobCsDetailsItemId(to),
    });
  }

  moveToItems(quoteChange: JobCsDetailsItem): void {
    // allow to move quote changes only if subheading is selected,
    // otherwise we do not know where to move this QC
    if (this.state.project(queryJobCsSelectedSubheadingId)) {
      const costCenters = this.state.project(queryJobCsSettings).ccClone
        ? this.state.project(queryJobCsCurrentSubheadingCostCenters).map(jobCsDetailsCcClone) : [];

      this.state.mutate({
        type: JobCsDetailsStateEventType.MoveToItems,
        headingId: this.state.project(queryJobCsSelectedHeadingId),
        subheadingId: this.state.project(queryJobCsSelectedSubheadingId),
        quoteChange: setEntityField(quoteChange, 'costCenters', costCenters),
      });
    }
  }

  backToQuoteChanges(item: JobCsDetailsItem): void {
    this.state.mutate({
      type: JobCsDetailsStateEventType.BackToQuoteChanges,
      headingId: this.state.project(queryJobCsSelectedHeadingId),
      subheadingId: this.state.project(queryJobCsSelectedSubheadingId),
      item: setEntityField(item, 'costCenters', []),
    });
  }

  setCostCenterField(id: ID, name: string, value: any): void {
    this.state.mutate({
      type: JobCsDetailsStateEventType.CostCenterSetField,
      headingId: this.state.project(queryJobCsSelectedHeadingId),
      subheadingId: this.state.project(queryJobCsSelectedSubheadingId),
      itemId: this.state.project(queryJobCsSelectedItemId),
      id,
      name,
      value,
    });
  }

  costCenterAdd(): void {
    this.dialogs.openWithInput(costCenterFinderDialogRoute, <CostCenterFinderDialogConfig>{
      multiselection: true,
      preselected: this.state.project(queryJobCsCurrentCostCenters).map((cc) => cc.costCenterId),
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe((costCenters: CostCenterFinderListItem[]) => {
        this.state.mutate({
          type: JobCsDetailsStateEventType.CostCenterAdd,
          headingId: this.state.project(queryJobCsSelectedHeadingId),
          subheadingId: this.state.project(queryJobCsSelectedSubheadingId),
          itemId: this.state.project(queryJobCsSelectedItemId),
          entities: costCenters.map(jobCsDetailsCcFromFinder),
        });
      });
  }

  costCenterRemove(cc: JobCsDetailsCc): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_cs_cc_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.state.mutate({
        type: JobCsDetailsStateEventType.CostCenterRemove,
        headingId: this.state.project(queryJobCsSelectedHeadingId),
        subheadingId: this.state.project(queryJobCsSelectedSubheadingId),
        itemId: this.state.project(queryJobCsSelectedItemId),
        id: getJobCsDetailsCcId(cc),
      }));
  }

  setQuoteChangeField(id: ID, name: string, value: any): void {
    this.state.mutate({
      type: JobCsDetailsStateEventType.QuoteChangeSetField,
      id,
      name,
      value,
    });
  }

  setSetting(name: keyof JobCsDetailsSettings, value: boolean): void {
    this.state.mutate({ type: JobCsDetailsStateEventType.SettingSetField, name, value });
  }

  private handleErrors(): void {
    if (!this.state.project(queryJobCsMasterInfoHasErrors)) {
      this.notifications.error('msg_he_incorrect_data_in_tables');
    }
    this.state.mutate({ type: JobCsDetailsStateEventType.NavigateToInvalid });
  }

  private validateQuoteChangesAction(): LogicalAction<JobCsDetailsSaveFlowState> {
    return booleanStreamAction(() =>
      isEmpty(this.state.project(queryJobCsQuoteChanges))
        ? of$(true)
        : this.dialogs.confirmation(yesNoConfirmation('msg_cs_other_qc_report')).pipe(map$(yesAnswer)));
  }
}
